import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { io } from 'socket.io-client';

const ProjectionScreen = () => {
  const { slug, id } = useParams();
  const [photos, setPhotos] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [event, setEvent] = useState(null);

  // WebRTC State
  const [isLive, setIsLive] = useState(false);
  const remoteVideoRef = React.useRef(null);
  const socketRef = React.useRef(null);
  const peerConnectionRef = React.useRef(null);

  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:11200';
  const frontendUrl = process.env.REACT_APP_FRONTEND_URL || window.location.origin;

  const guestUploadLink = `${frontendUrl}/${slug}/events/${id}/upload`;
  const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(guestUploadLink)}`;

  const fetchPhotos = useCallback(async () => {
    try {
      // It's a public route for approved photos
      const res = await axios.get(`${apiUrl}/api/events/${id}/photos`);
      const newPhotos = res.data;
      
      setPhotos(prevPhotos => {
        // Only update state if length is different or array is new to not reset carousel if not needed
        if (prevPhotos.length !== newPhotos.length) {
          return newPhotos;
        }
        // If they are exactly the same, return prev to avoid triggering re-renders
        // A simple dirty check based on first element ID
        if (prevPhotos.length > 0 && newPhotos.length > 0 && prevPhotos[0]._id !== newPhotos[0]._id) {
            return newPhotos;
        }
        return prevPhotos;
      });
    } catch (err) {
      console.error('Error fetching projection photos:', err);
    }
  }, [apiUrl, id]);

  const fetchEvent = useCallback(async () => {
    try {
      // Need slug? We don't have slug in this projection route.
      // the event route in backend might need slug, but the public /api/events/:id might work.
      // We will try without slug, and if not just ignore event title.
      const res = await axios.get(`${apiUrl}/api/events/${id}`);
      setEvent(res.data);
    } catch (err) {
      console.error(err);
    }
  }, [apiUrl, id]);

  useEffect(() => {
    fetchEvent();
    fetchPhotos();
    // Poll for new photos every 5 seconds
    const interval = setInterval(fetchPhotos, 5000);
    return () => clearInterval(interval);
  }, [fetchPhotos, fetchEvent]);

  // Carousel logic: change photo every 8 seconds
  useEffect(() => {
    if (photos.length <= 1 || isLive) return;
    
    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % photos.length);
    }, 8000);
    
    return () => clearInterval(timer);
  }, [photos.length, isLive]);

  // WebRTC Receiver Logic
  useEffect(() => {
    const socket = io(apiUrl);
    socketRef.current = socket;

    socket.on('connect', () => {
      socket.emit('join-event', id);
      // Tell broadcaster we are ready to receive offer
      socket.emit('viewer-joined', id);
    });

    socket.on('stream-started', () => {
      setIsLive(true);
      socket.emit('viewer-joined', id);
    });
    
    socket.on('stream-stopped', () => {
      setIsLive(false);
      if (remoteVideoRef.current) {
        remoteVideoRef.current.srcObject = null;
      }
    });

    socket.on('offer', async (data) => {
      if (data.eventId !== id) return;
      
      const pc = new RTCPeerConnection({
        iceServers: [{ urls: 'stun:stun.l.google.com:19302' }]
      });
      peerConnectionRef.current = pc;

      pc.ontrack = (event) => {
        if (remoteVideoRef.current) {
          remoteVideoRef.current.srcObject = event.streams[0];
          // Intento forzar play por si el autoplay falla
          remoteVideoRef.current.play().catch(e => console.warn("Autoplay blocked", e));
        }
      };

      pc.onicecandidate = (event) => {
        if (event.candidate) {
          socket.emit('ice-candidate', { eventId: id, sender: socket.id, target: data.sender, candidate: event.candidate });
        }
      };

      await pc.setRemoteDescription(new RTCSessionDescription(data.offer));
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      socket.emit('answer', { eventId: id, sender: socket.id, target: data.sender, answer });
    });

    socket.on('ice-candidate', async (data) => {
      if (data.eventId !== id || !peerConnectionRef.current) return;
      await peerConnectionRef.current.addIceCandidate(new RTCIceCandidate(data.candidate));
    });

    return () => {
      socket.disconnect();
      if (peerConnectionRef.current) peerConnectionRef.current.close();
    };
  }, [apiUrl, id]);

  return (
    <div className="fixed inset-0 bg-black text-white flex flex-col overflow-hidden z-[9999]">
      {/* Background with subtle animation */}
      {photos[currentIndex] && (
        <div 
          className="absolute inset-0 z-0 bg-cover bg-center opacity-30 blur-2xl animate-pulse"
          style={{ backgroundImage: `url(${photos[currentIndex]?.url})`, transition: 'background-image 1s ease-in-out' }}
        ></div>
      )}

      {/* Main Content Area */}
      <div className="relative z-10 flex-1 flex items-center justify-center p-8">
        {isLive ? (
          <div className="w-full h-full max-w-[100vw] max-h-[100vh] flex items-center justify-center animate-in fade-in zoom-in duration-500">
            <video
              ref={remoteVideoRef}
              autoPlay
              playsInline
              muted
              className="w-full h-full object-contain drop-shadow-2xl rounded-xl"
            />
            {/* Live Indicator */}
            <div className="absolute top-8 left-8 bg-red-500/80 backdrop-blur-md px-6 py-2 rounded-full border border-white/10 shadow-lg flex items-center gap-3 animate-pulse">
              <span className="w-3 h-3 rounded-full bg-white"></span>
              <span className="text-white font-black tracking-widest text-lg">EN VIVO</span>
            </div>
          </div>
        ) : photos.length === 0 ? (
          <div className="text-center animate-pulse">
            <h1 className="text-4xl md:text-6xl font-black mb-4 tracking-tighter text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-500">
              {event?.title ? `¡Bienvenidos a ${event.title}!` : 'Cargando...'}
            </h1>
            <p className="text-white/60 text-xl font-medium">Escanea el código para compartir tus fotos en pantalla grande</p>
          </div>
        ) : (
          <div className="w-full h-full max-w-7xl max-h-[85vh] flex items-center justify-center animate-in fade-in zoom-in duration-1000">
            <img 
              key={photos[currentIndex]?._id} // force re-animation on change
              src={photos[currentIndex]?.url} 
              alt="Live Event" 
              className="max-w-full max-h-full object-contain drop-shadow-2xl rounded-xl"
            />
            {/* Overlay the uploader name */}
            <div className="absolute bottom-12 right-12 bg-black/50 backdrop-blur-md px-6 py-3 rounded-full border border-white/10 shadow-lg animate-in slide-in-from-bottom-10 fade-in duration-700">
              <span className="text-white font-bold tracking-widest text-lg">
                 📸 {photos[currentIndex]?.uploadedBy || 'Anónimo'}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Footer bar with branding and QR */}
      <div className="relative z-20 bg-black/60 backdrop-blur-xl border-t border-purple-500/20 px-8 py-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="bg-white p-1 rounded-xl shadow-lg shadow-purple-500/20">
            <img src={qrCodeUrl} alt="QR Code" className="w-20 h-20 rounded-md" />
          </div>
          <div>
            <h3 className="text-xl font-black bg-gradient-to-r from-purple-400 to-pink-500 bg-clip-text text-transparent">
              {event?.title || '¡Comparte tus fotos!'}
            </h3>
            <p className="text-white/60 font-medium text-sm">Escanea este código con tu celular</p>
          </div>
        </div>

        <div className="text-right">
          <p className="text-[10px] font-black uppercase tracking-[3px] text-white/40">Desarrollado por</p>
          <p className="text-xl font-black tracking-tighter text-white">PASSAR</p>
        </div>
      </div>
    </div>
  );
};

export default ProjectionScreen;
